import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { thisWeekItems, ThisWeekItem } from '../../../mocks/thisWeek';
import SaveToCalendar from './SaveToCalendar';

// ── date helpers ──────────────────────────────────────────────────────────────

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function resolveDay(dayName?: string): Date | null {
  if (!dayName || dayName === 'Any') return null;
  const idx = DAY_NAMES.indexOf(dayName);
  if (idx === -1) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = (idx - today.getDay() + 7) % 7;
  const result = new Date(today);
  result.setDate(today.getDate() + diff);
  return result;
}

function formatResolvedDate(date: Date | null, fallback: string): string {
  if (!date) return fallback;
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatWeekRange(): string {
  const today = new Date();
  const mon = new Date(today);
  mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${mon.toLocaleDateString('en-US', opts)} – ${sun.toLocaleDateString('en-US', opts)}`;
}

// ── type config ───────────────────────────────────────────────────────────────

type StandardType = Exclude<ThisWeekItem['type'], 'member-perk'>;

const typeConfig: Record<StandardType, { accent: string; tagStyle: string; icon: string }> = {
  'mommy-mingle': {
    accent: 'border-pink-400',
    tagStyle: 'bg-pink-50 text-pink-600',
    icon: 'ri-heart-fill',
  },
  'local-event': {
    accent: 'border-amber-400',
    tagStyle: 'bg-amber-50 text-amber-600',
    icon: 'ri-calendar-event-line',
  },
  outing: {
    accent: 'border-emerald-400',
    tagStyle: 'bg-emerald-50 text-emerald-600',
    icon: 'ri-map-pin-2-line',
  },
};

// ── standard card ─────────────────────────────────────────────────────────────

interface ItemCardProps {
  item: ThisWeekItem;
  resolvedDate: Date | null;
}

function ItemCard({ item, resolvedDate }: ItemCardProps) {
  const config = typeConfig[item.type as StandardType];
  const isExternal = item.link?.startsWith('http');
  const dateLabel = formatResolvedDate(resolvedDate, 'This week');
  const isToday =
    resolvedDate !== null &&
    resolvedDate.toDateString() === new Date().toDateString();

  return (
    <div className={`flex flex-col bg-white rounded-xl border-l-4 ${config.accent} border border-stone-100 p-5 h-full`}>
      {/* Tag row */}
      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${config.tagStyle}`}>
          <i className={`${config.icon} text-xs`}></i>
          {item.tag}
        </span>
        <SaveToCalendar
          title={item.title}
          location={item.location ?? ''}
          detail={item.detail}
          eventDate={resolvedDate}
          startHour={item.startHour}
          durationMins={item.durationMins}
        />
      </div>

      <h3 className="font-bold text-stone-800 text-base mb-2 leading-snug">{item.title}</h3>
      <p className="text-sm text-stone-500 leading-relaxed mb-4 flex-1">{item.detail}</p>

      <div className="flex flex-col gap-1.5 mb-4">
        <div className="flex items-center gap-2 text-xs">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-calendar-line text-stone-400"></i>
          </div>
          <span className={`font-semibold ${isToday ? 'text-pink-600' : 'text-stone-700'}`}>
            {isToday ? 'Today' : dateLabel}
          </span>
          {item.time && <span className="text-stone-400">· {item.time}</span>}
        </div>
        {item.location && (
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-stone-400"></i>
            </div>
            <span>{item.location}</span>
          </div>
        )}
      </div>

      {item.link && item.linkLabel && (
        isExternal ? (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-700 hover:text-pink-600 transition-colors cursor-pointer group whitespace-nowrap"
          >
            {item.linkLabel}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
            </div>
          </a>
        ) : (
          <Link
            to={item.link}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-700 hover:text-pink-600 transition-colors cursor-pointer group whitespace-nowrap"
          >
            {item.linkLabel}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
            </div>
          </Link>
        )
      )}
    </div>
  );
}

// ── member perk card ──────────────────────────────────────────────────────────

function MemberPerkCard({ item }: { item: ThisWeekItem }) {
  return (
    <div className="flex flex-col h-full rounded-xl border border-rose-200/70 border-l-4 border-l-rose-400 bg-gradient-to-br from-rose-50 via-pink-50/40 to-white p-5 relative overflow-hidden">
      {/* subtle decorative ring */}
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-rose-100/50 pointer-events-none" />
      <div className="absolute -bottom-4 -right-2 w-16 h-16 rounded-full bg-pink-100/40 pointer-events-none" />

      {/* Tag */}
      <div className="flex items-center gap-1.5 mb-3 relative z-10">
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-600">
          <i className="ri-vip-crown-fill text-xs"></i>
          Member Perk
        </span>
      </div>

      {/* Title */}
      <h3 className="font-extrabold text-stone-800 text-base mb-2 leading-snug relative z-10">
        Early Event Access
      </h3>

      {/* Detail — split on \n */}
      <div className="text-sm text-stone-500 leading-relaxed mb-4 flex-1 relative z-10">
        <p className="mb-2">Get early access to event RSVPs before they go public.</p>
        <p>Skip the wait and secure your spot first.</p>
      </div>

      {/* Benefit pills */}
      <div className="flex flex-wrap gap-1.5 mb-4 relative z-10">
        {['Early RSVPs', 'Priority Spots', 'Member Pricing'].map((pill) => (
          <span
            key={pill}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white border border-rose-100 text-rose-500"
          >
            <i className="ri-check-line text-[10px]"></i>
            {pill}
          </span>
        ))}
      </div>

      {/* CTA */}
      <Link
        to="/membership"
        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap relative z-10"
      >
        <i className="ri-lock-unlock-line text-xs"></i>
        Unlock Early Access
      </Link>
    </div>
  );
}

// ── section ───────────────────────────────────────────────────────────────────

export default function ThisWeekForMoms() {
  const weekRange = useMemo(() => formatWeekRange(), []);
  const resolvedDates = useMemo(
    () => thisWeekItems.map((item) => resolveDay(item.day)),
    [],
  );

  return (
    <section className="py-14 md:py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-pink-100 rounded-full mb-3">
            <i className="ri-sun-line text-pink-500 text-xs"></i>
            <span className="text-xs font-semibold text-pink-600 tracking-wide uppercase">
              Week of {weekRange}
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-stone-800 mb-1 tracking-tight">
            This Week for Moms
          </h2>
          <p className="text-stone-400 text-sm md:text-base font-light">
            Start here if you just need something easy.
          </p>
        </div>

        {/* Cards — horizontal scroll on mobile/tablet, 5-col grid on large */}
        <div className="flex gap-4 overflow-x-auto pb-2 lg:pb-0 lg:grid lg:grid-cols-5 lg:overflow-visible snap-x snap-mandatory lg:snap-none">
          {thisWeekItems.map((item, i) => (
            <div
              key={item.id}
              className="min-w-[280px] sm:min-w-[300px] lg:min-w-0 snap-start flex-shrink-0 lg:flex-shrink"
            >
              {item.type === 'member-perk'
                ? <MemberPerkCard item={item} />
                : <ItemCard item={item} resolvedDate={resolvedDates[i]} />
              }
            </div>
          ))}
        </div>

        {/* Footer nudge */}
        <div className="mt-7 flex items-center justify-between">
          <p className="text-xs text-stone-400">
            Updated weekly · Curated for Gwinnett moms
          </p>
          <Link
            to="/events"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-pink-600 transition-colors cursor-pointer group whitespace-nowrap"
          >
            See all events
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
            </div>
          </Link>
        </div>

      </div>
    </section>
  );
}
